import React from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import {
    computeBoundaryNeeds,
    computeNeighbors
} from '../../../../components/ListNav/computeNeighbors';
import type { ListNeighborsResult, ListSnapshot, Row } from '../../../../components/ListNav/types';
import {
    getListBackTo,
    useListSnapshot,
    writeListSnapshot
} from '../../../../components/ListNav/useListSnapshot';
import useGetCheckinMembers, { GetCheckinMembersParams } from '../../api/useGetCheckinMembers';
import useGetOrganizationMembers, {
    GetOrganizationMembersParams
} from '../../api/useGetOrganizationMembers';
import { CheckinMembersResponse, OrganizationMemberListPaginated } from '../../types';
import getMemberUrl from '../../utils/getMemberUrl';

export type MemberListKind = 'members' | 'checkin-members';
export type MemberListQuery = GetOrganizationMembersParams | GetCheckinMembersParams;
export type MemberListSnapshot = ListSnapshot<MemberListQuery>;

const SESSION_KEY = 'memberListSnapshot';
const FALLBACK_BACK_TO = '/members';

export const writeMemberListSnapshot = (snapshot: MemberListSnapshot) =>
    writeListSnapshot(SESSION_KEY, snapshot);

export const getMemberListBackTo = (locationState: unknown) =>
    getListBackTo(locationState, SESSION_KEY, FALLBACK_BACK_TO);

/** The member list the user last opened a member from. */
export const useMemberListSnapshot = () => useListSnapshot<MemberListQuery>(SESSION_KEY);

const formatName = (firstName?: string | null, lastName?: string | null) => {
    const combined = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return combined.length ? combined : 'N/A';
};

const normalizeMembersList = (data?: OrganizationMemberListPaginated): Row[] => {
    if (!data?.list) return [];
    return data.list.map((item) => ({
        name: formatName(item.first_name, item.last_name),
        url: item.readmitted_to
            ? getMemberUrl(null, item.readmitted_to)
            : getMemberUrl(item.hero_user_id, item.member_id)
    }));
};

const normalizeCheckinList = (data?: CheckinMembersResponse): Row[] => {
    if (!data?.members) return [];
    return data.members.map(({ member }) => ({
        name: formatName(member.first_name, member.last_name),
        url: member.id ? getMemberUrl(null, member.id) : getMemberUrl(member.hero_user_id, null)
    }));
};

// Carry the active tab (path segment after the member id) onto a neighbor URL so
// prev/next land on the same tab. Empty segment = Overview, which needs no suffix.
const appendTabSegment = (url: string, tabSegment: string) => {
    if (!tabSegment) return url;
    const [path, query] = url.split('?');
    return `${path}/${tabSegment}${query ? `?${query}` : ''}`;
};

const useMemberListNeighbors = (): ListNeighborsResult => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const role = searchParams.get('role') === 'caregiver' ? 'caregiver' : 'primary_user';

    const base = `/members/${id}`;
    const tabSegment = location.pathname.startsWith(base)
        ? location.pathname.slice(base.length).replace(/^\//, '')
        : '';

    const snapshot = useMemberListSnapshot();

    const isCheckin = snapshot?.listKind === 'checkin-members';
    const listQuery: MemberListQuery = snapshot?.listQuery ?? {};

    const membersQ = useGetOrganizationMembers(listQuery as GetOrganizationMembersParams, {
        enabled: !!snapshot && !isCheckin
    });
    const checkinQ = useGetCheckinMembers(listQuery as GetCheckinMembersParams, {
        enabled: !!snapshot && isCheckin
    });

    const rows = React.useMemo(
        () =>
            isCheckin ? normalizeCheckinList(checkinQ.data) : normalizeMembersList(membersQ.data),
        [isCheckin, checkinQ.data, membersQ.data]
    );

    const limit = Number((listQuery as { limit?: number | string }).limit ?? 10) || 10;
    const offsetNum = Number((listQuery as { offset?: number | string }).offset ?? 0) || 0;
    const totalCount = (isCheckin ? checkinQ.data?.total_count : membersQ.data?.total_count) ?? 0;

    const currentKey = id ? `/members/${id}?role=${role}` : '';
    const currentIdx = currentKey ? rows.findIndex((r) => r.url === currentKey) : -1;

    const { needPrevPage, needNextPage } = computeBoundaryNeeds(
        currentIdx,
        rows,
        offsetNum,
        limit,
        totalCount
    );

    const prevPageQuery = { ...listQuery, offset: String(offsetNum - limit) } as MemberListQuery;
    const nextPageQuery = { ...listQuery, offset: String(offsetNum + limit) } as MemberListQuery;

    const prevPageMembersQ = useGetOrganizationMembers(
        prevPageQuery as GetOrganizationMembersParams,
        { enabled: !!snapshot && !isCheckin && needPrevPage }
    );
    const nextPageMembersQ = useGetOrganizationMembers(
        nextPageQuery as GetOrganizationMembersParams,
        { enabled: !!snapshot && !isCheckin && needNextPage }
    );
    const prevPageCheckinQ = useGetCheckinMembers(prevPageQuery as GetCheckinMembersParams, {
        enabled: !!snapshot && isCheckin && needPrevPage
    });
    const nextPageCheckinQ = useGetCheckinMembers(nextPageQuery as GetCheckinMembersParams, {
        enabled: !!snapshot && isCheckin && needNextPage
    });

    const prevPageRows = React.useMemo(
        () =>
            isCheckin
                ? normalizeCheckinList(prevPageCheckinQ.data)
                : normalizeMembersList(prevPageMembersQ.data),
        [isCheckin, prevPageCheckinQ.data, prevPageMembersQ.data]
    );
    const nextPageRows = React.useMemo(
        () =>
            isCheckin
                ? normalizeCheckinList(nextPageCheckinQ.data)
                : normalizeMembersList(nextPageMembersQ.data),
        [isCheckin, nextPageCheckinQ.data, nextPageMembersQ.data]
    );

    const { prev, next, position } = computeNeighbors({
        rows,
        currentIdx,
        offset: offsetNum,
        limit,
        totalCount,
        snapshot,
        prevPageRows,
        nextPageRows
    });

    return {
        prev: prev ? { ...prev, url: appendTabSegment(prev.url, tabSegment) } : prev,
        next: next ? { ...next, url: appendTabSegment(next.url, tabSegment) } : next,
        position,
        backTo: snapshot?.from ?? FALLBACK_BACK_TO
    };
};

export default useMemberListNeighbors;
